import { useEffect, useMemo, useState } from "react";
import { Typography, Spin, Modal, message } from "antd";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import { getProyectosInformeTecnicoFinal } from "../../services/informeTecnicoFinal/informeTecnicoFinalService";
import getInformeFinalPlanOperativo from "../../services/plaOperativo/informeFinalPlanOperativoService";
import ProyectosFilters from "./components/ProyectosFilters";
import ProyectosCards from "./components/ProyectosCards";
import ProyectosTable from "./components/ProyectosTable";
import FileManagerPP from "../poaReporte/FileManagerPP";

const { Title, Text } = Typography;

export default function InformeTecnicoFinalPage() {
  const appName = "informe_tecnico_final";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionAccess = permissions.priv_access === "Y";

  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState(null);
  const [estadoFinalizacion, setEstadoFinalizacion] = useState(null);
  const [viewMode, setViewMode] = useState("cards");

  const [modalVisible, setModalVisible] = useState(false);
  const [dataFilterProyecto, setDataFilterProyecto] = useState(null);

  const cargarProyectos = async () => {
    setLoading(true);
    const { data } = await getProyectosInformeTecnicoFinal();
    setProyectos(data);
    setLoading(false);
  };

  useEffect(() => {
    if (permissionAccess) cargarProyectos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionAccess]);

  const estadosDisponibles = useMemo(
    () => [...new Set(proyectos.map((p) => p.ac_estado).filter(Boolean))],
    [proyectos]
  );

  const proyectosFiltrados = useMemo(() => {
    const term = search.trim().toLowerCase();

    return proyectos.filter((p) => {
      const matchSearch =
        !term ||
        p.codigo?.toLowerCase().includes(term) ||
        p.proyecto?.toLowerCase().includes(term) ||
        p.Nombre_proyectoAsociado?.toLowerCase().includes(term);

      const matchEstado = !estado || p.ac_estado === estado;
      const matchEstadoFinalizacion =
        !estadoFinalizacion || (p.estadoFinalizacion || "pendiente") === estadoFinalizacion;

      return matchSearch && matchEstado && matchEstadoFinalizacion;
    });
  }, [proyectos, search, estado, estadoFinalizacion]);

  // Abre FileManagerPP directamente en esta misma página (sin navegar a
  // /planOperativo). El código de tarea que contiene el Informe Técnico
  // Final varía por proyecto (4.1.1 a 4.1.6 según su estructura de POA), así
  // que se busca por el texto del indicador en vez de un código fijo.
  const handleSelect = async (proyecto) => {
    const response = await getInformeFinalPlanOperativo(proyecto.codigo);
    if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
      setDataFilterProyecto(response.data);
      setModalVisible(true);
    } else {
      message.warning("No se encontraron datos para este proyecto.");
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setDataFilterProyecto(null);
  };

  const handleEstadoChange = (oidProyecto, nuevoEstado) => {
    setProyectos((prev) =>
      prev.map((p) => (p.oid === oidProyecto ? { ...p, estadoFinalizacion: nuevoEstado } : p))
    );
  };

  const handleReset = () => {
    setSearch("");
    setEstado(null);
    setEstadoFinalizacion(null);
  };

  if (!permissionAccess) {
    return <div className="container">No tiene permisos para acceder a este módulo</div>;
  }

  return (
    <div className="container">
      <div className="row mb-2 d-flex align-items-center">
        <div className="col-md-8 linea_separador mb-2 d-flex align-items-center">
          <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
            <p>PROYECTO UTF <br /> COL 160 COL</p>
          </div>
          <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
            <p>Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET</p>
          </div>
        </div>
        <div className="col-md-4 d-flex justify-content-center align-items-center flex-column">
          <Title level={3} style={{ color: "#042956", marginBottom: 4 }}>
            Informe Técnico Final
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Consulta la documentación de cierre de cada proyecto (Informe Técnico Final del POA)
          </Text>
        </div>
      </div>

      <ProyectosFilters
        search={search}
        onSearchChange={setSearch}
        estado={estado}
        onEstadoChange={setEstado}
        estadosDisponibles={estadosDisponibles}
        estadoFinalizacion={estadoFinalizacion}
        onEstadoFinalizacionChange={setEstadoFinalizacion}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onReset={handleReset}
      />

      {loading && proyectos.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : viewMode === "cards" ? (
        <ProyectosCards proyectos={proyectosFiltrados} onSelect={handleSelect} onEstadoChange={handleEstadoChange} />
      ) : (
        <ProyectosTable
          proyectos={proyectosFiltrados}
          loading={loading}
          onSelect={handleSelect}
          onEstadoChange={handleEstadoChange}
        />
      )}

      <Modal
        title={<span className="custom-modal-title">Medios de verificación</span>}
        open={modalVisible}
        onCancel={handleCloseModal}
        destroyOnHidden
        footer={null}
        width="95%"
        style={{ top: 20 }}
        zIndex={2000}
      >
        {Array.isArray(dataFilterProyecto) && dataFilterProyecto.length > 0 ? (
          <FileManagerPP
            codigo_tarea={{
              proyecto: dataFilterProyecto[0]?.pp,
              codActividad: dataFilterProyecto[0]?.codigo_actividad,
              actividad: dataFilterProyecto[0]?.descripcion,
              codTarea: dataFilterProyecto[0]?.codigo_tarea,
              tarea:
                dataFilterProyecto[0]?.codigo_tarea +
                " - " +
                dataFilterProyecto[0]?.tarea_descripcion,
              codIndicador: dataFilterProyecto[0]?.indicador_id,
              indicador: dataFilterProyecto[0]?.indicador,
              codMV: dataFilterProyecto[0]?.medio_verificacion_id,
              medioVerificacion: dataFilterProyecto[0]?.medio_verificacion,
            }}
            select_proyect={dataFilterProyecto}
          />
        ) : (
          <p>No hay información para mostrar</p>
        )}
      </Modal>
    </div>
  );
}
